import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle, AlertTriangle, Plug, RefreshCw, Eye, EyeOff, Link as LinkIcon, Share2,
} from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/hooks/useToast';
import {
  getZernioCredentials,
  saveZernioCredentials,
  testZernioConnection,
  refreshZernioPlatforms,
  platformLabel,
} from '@/services/zernio.service';
import { PROVIDER_HELP } from '@/services/marketingIntegrations.service';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';

/** Mirrors SaveZernioCredentialsRequest so a doomed save never leaves the browser. */
const KEY_PATTERN = /^sk_[a-f0-9]{64}$/i;

function describeApiError(e: any): string {
  return e?.response?.data?.error?.message ?? e?.message ?? 'Request failed';
}

/**
 * Zernio key entry for Admin > Integrations > Marketing.
 *
 * Saving here writes the SAME module_account_settings row that Corp-App's
 * super-admin Module Settings UI owns — one source of truth, two doors. The key
 * is verified on save, so a bad paste is reported immediately rather than
 * surfacing later as "unverified" on Social Posts.
 *
 * The raw key is never read back: the server returns it masked, and the input
 * is cleared once stored. Rendered only when the Zernio module is active (see
 * MarketingIntegrationsAdmin), matching the API's fail-closed gate.
 *
 * Locked (plan 2026-10-10-001 U7: R8, R18): the status, masked key and
 * connected platforms show; there is no key entry or Save, and no Test
 * Connection or Refresh Platforms (both record their result server-side).
 * Nothing says why.
 */
export function ZernioIntegrationCard() {
  const locked = useAdminPageLevel() === 'locked';
  const toast = useToast();
  const qc = useQueryClient();
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'zernio', 'credentials'],
    queryFn: getZernioCredentials,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin', 'zernio', 'credentials'] });
    qc.invalidateQueries({ queryKey: ['admin', 'marketing-integrations'] });
  };

  const saveMut = useMutation({
    mutationFn: () => saveZernioCredentials(apiKey.trim()),
    onSuccess: (res) => {
      // Clear the raw key the moment it is stored — it is never read back.
      setApiKey('');
      setShowKey(false);
      if (res.isVerified) {
        toast.success('Zernio connected');
      } else {
        toast.error(res.error ?? 'Key saved but could not be verified');
      }
      invalidate();
    },
    onError: (e: any) => toast.error(describeApiError(e)),
  });

  const testMut = useMutation({
    mutationFn: testZernioConnection,
    onSuccess: (res) => {
      if (res.valid) toast.success('Zernio key is valid');
      else toast.error(res.error ?? 'Key is not valid');
      invalidate();
    },
    onError: (e: any) => toast.error(describeApiError(e)),
  });

  const refreshMut = useMutation({
    mutationFn: refreshZernioPlatforms,
    onSuccess: (res) => {
      toast.success(`${res.connectedPlatforms.length} platform(s) connected`);
      invalidate();
    },
    onError: (e: any) => toast.error(describeApiError(e)),
  });

  const configured = data?.configured ?? false;
  const isVerified = data?.isVerified ?? false;
  const platforms = data?.connectedPlatforms ?? [];
  const typed = apiKey.trim();
  const keyLooksValid = KEY_PATTERN.test(typed);

  return (
    <Card className="space-y-0">
      <CardHeader className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg grid place-items-center bg-indigo-50 text-indigo-600">
            <Share2 className="h-5 w-5" />
          </div>
          <div>
            <div className="font-semibold text-slate-800">Zernio</div>
            <div className="mt-0.5">
              {!configured ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-500">
                  <Plug className="h-3 w-3" /> Not connected
                </span>
              ) : isVerified ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700">
                  <CheckCircle className="h-3 w-3" /> Connected
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-700">
                  <AlertTriangle className="h-3 w-3" /> Unverified
                </span>
              )}
            </div>
          </div>
        </div>
      </CardHeader>

      <CardBody className="space-y-4">
        <p className="text-sm text-slate-500">{PROVIDER_HELP.zernio}</p>

        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Spinner className="h-4 w-4" /> Loading…
          </div>
        ) : (
          <div className="space-y-3">
            {configured && (
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                <div className="text-xs uppercase tracking-wide text-slate-400">Current API key</div>
                <div className="font-mono">{data?.apiKey}</div>
              </div>
            )}

            {configured && !isVerified && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                The stored key could not be verified with Zernio.
                {!locked && ' Publishing stays disabled until a test succeeds.'}
              </div>
            )}

            {!locked && (
            <div className="space-y-2">
              <label className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                {configured ? 'Replace API key' : 'Zernio API key'}
              </label>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Input
                    type={showKey ? 'text' : 'password'}
                    placeholder="sk_…"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    className="font-mono text-sm pr-9"
                  />
                  <button
                    type="button"
                    onClick={() => setShowKey((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    aria-label={showKey ? 'Hide API key' : 'Show API key'}
                  >
                    {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending || !keyLooksValid}>
                  <LinkIcon className="h-4 w-4 mr-1" />
                  {saveMut.isPending ? 'Saving…' : 'Save'}
                </Button>
              </div>
              {typed !== '' && !keyLooksValid && (
                <div className="text-xs text-amber-700">
                  A Zernio key is <code>sk_</code> followed by 64 hexadecimal characters.
                </div>
              )}
              <div className="text-xs text-slate-500">
                Find your key in the Zernio dashboard under Settings → API. A Prospera
                administrator can also set it in Module Settings — both write the same place.
              </div>
            </div>
            )}

            {configured && (
              <>
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-400 mb-1.5">
                    Connected platforms
                  </div>
                  {platforms.length ? (
                    <div className="flex flex-wrap gap-1.5">
                      {platforms.map((p) => (
                        <Badge key={p} variant="info" size="sm">{platformLabel(p)}</Badge>
                      ))}
                    </div>
                  ) : (
                    <div className="text-xs text-slate-500">
                      {isVerified
                        ? locked
                          ? 'No social accounts are connected yet.'
                          : 'The key works, but no social accounts are connected yet. Connect them in the Zernio dashboard, then hit Refresh.'
                        : 'No platforms to show until the key verifies.'}
                    </div>
                  )}
                </div>

                {!locked && (
                <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                  <Button variant="ghost" onClick={() => testMut.mutate()} disabled={testMut.isPending}>
                    {testMut.isPending ? <Spinner className="h-4 w-4 mr-1" /> : <CheckCircle className="h-4 w-4 mr-1" />}
                    Test Connection
                  </Button>
                  <Button variant="ghost" onClick={() => refreshMut.mutate()} disabled={refreshMut.isPending}>
                    <RefreshCw className="h-4 w-4 mr-1" />
                    {refreshMut.isPending ? 'Refreshing…' : 'Refresh Platforms'}
                  </Button>
                </div>
                )}
              </>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
}
